import { getOperation } from '$lib/apis/operations/api'

export type OperationStatus = 'pending' | 'running' | 'done' | 'failed'

export type OperationDetails = {
  phase?: string
  library?: string
  series?: string
  libraryIndex?: number
  libraryTotal?: number
  seriesIndex?: number
  seriesTotal?: number
  progressPercent?: number
}

export type OperationToast = {
  id: string
  title: string
  description?: string
  status: OperationStatus
  type?: string
  error?: string
  details?: OperationDetails
  createdAt: number
  /** When set, toast is auto-dismissing; Progress drains until this timestamp. */
  dismissEndsAt?: number
  dismissMs?: number
}

const POLL_MS = 1000
export const DISMISS_DONE_MS = 5000
export const DISMISS_FAILED_MS = 8000

/** Stackable operation toasts (top-right). */
export const operationToasts = $state<OperationToast[]>([])

/**
 * Bumps when a tracked operation completes successfully.
 * Series list/detail views can read this in an $effect to re-fetch.
 */
export const seriesDataEpoch = $state({ value: 0 })

const tracked = new Map<
  string,
  { server: any; timer?: ReturnType<typeof setInterval> }
>()

function normalizeStatus(s: string | undefined): OperationStatus {
  if (s === 'running' || s === 'done' || s === 'failed' || s === 'pending') return s
  return 'pending'
}

function parseOperationDetails(raw: unknown): OperationDetails | undefined {
  if (raw == null || raw === '') return undefined
  if (typeof raw === 'object') return raw as OperationDetails
  if (typeof raw !== 'string') return undefined
  try {
    return JSON.parse(raw) as OperationDetails
  } catch {
    return undefined
  }
}

function notifySeriesDataChanged() {
  seriesDataEpoch.value++
}

export function dismissOperationToast(id: string) {
  const t = tracked.get(id)
  if (t?.timer) clearInterval(t.timer)
  tracked.delete(id)
  const i = operationToasts.findIndex((x) => x.id === id)
  if (i !== -1) operationToasts.splice(i, 1)
}

function upsertToast(toast: OperationToast) {
  const i = operationToasts.findIndex((x) => x.id === toast.id)
  if (i === -1) {
    operationToasts.push(toast)
  } else {
    operationToasts[i] = { ...operationToasts[i], ...toast }
  }
}

function scheduleDismiss(id: string, status: OperationStatus) {
  const ms = status === 'failed' ? DISMISS_FAILED_MS : DISMISS_DONE_MS
  const i = operationToasts.findIndex((x) => x.id === id)
  if (i !== -1) {
    operationToasts[i] = {
      ...operationToasts[i],
      dismissEndsAt: Date.now() + ms,
      dismissMs: ms,
    }
  }
  setTimeout(() => dismissOperationToast(id), ms)
}

async function pollOnce(id: string) {
  const entry = tracked.get(id)
  if (!entry) return
  try {
    const data = await getOperation(entry.server, id)
    const op = data?.operation
    if (!op) return
    const status = normalizeStatus(op.status)
    const i = operationToasts.findIndex((x) => x.id === id)
    const prev = i === -1 ? null : operationToasts[i]
    const becameDone = status === 'done' && prev?.status !== 'done'
    upsertToast({
      id,
      title: prev?.title ?? 'Operation',
      description: prev?.description,
      status,
      type: prev?.type ?? op.type,
      error: op.error || undefined,
      details: parseOperationDetails(op.details),
      createdAt: prev?.createdAt ?? Date.now(),
    })
    if (status === 'done' || status === 'failed') {
      if (entry.timer) clearInterval(entry.timer)
      tracked.delete(id)
      scheduleDismiss(id, status)
      if (becameDone) notifySeriesDataChanged()
    }
  } catch (err) {
    console.error('operation poll failed', id, err)
  }
}

/** Start tracking a queued operation; shows toast and polls until terminal. */
export function trackOperation(opts: {
  server: any
  operation: { id?: string; status?: string; type?: string; error?: string; details?: unknown }
  title: string
  description?: string
}) {
  const id = opts.operation?.id
  if (!id || !opts.server) return

  if (tracked.has(id)) {
    const i = operationToasts.findIndex((x) => x.id === id)
    if (i !== -1) {
      operationToasts[i] = {
        ...operationToasts[i],
        title: opts.title,
        description: opts.description ?? operationToasts[i].description,
        type: opts.operation.type ?? operationToasts[i].type,
        details: parseOperationDetails(opts.operation.details) ?? operationToasts[i].details,
      }
    }
    return
  }

  const status = normalizeStatus(opts.operation.status)
  upsertToast({
    id,
    title: opts.title,
    description: opts.description,
    status,
    type: opts.operation.type,
    error: opts.operation.error || undefined,
    details: parseOperationDetails(opts.operation.details),
    createdAt: Date.now(),
  })

  if (status === 'done' || status === 'failed') {
    scheduleDismiss(id, status)
    if (status === 'done') notifySeriesDataChanged()
    return
  }

  const timer = setInterval(() => void pollOnce(id), POLL_MS)
  tracked.set(id, { server: opts.server, timer })
  void pollOnce(id)
}

export function phaseLabel(phase: string | undefined) {
  switch (phase) {
    case 'scanning':
      return 'Scanning'
    case 'refreshing':
      return 'Refreshing metadata'
    default:
      return undefined
  }
}

/** Label helpers for known operation types. */
export function operationTitle(type: string | undefined, fallback = 'Operation') {
  switch (type) {
    case 'refresh_series':
      return 'Refreshing metadata'
    case 'fix_match':
      return 'Fixing match'
    case 'unmatch_series':
      return 'Unmatching series'
    case 'scan_libraries':
      return 'Scanning libraries'
    case 'scan_and_refresh_libraries':
      return 'Scanning & refreshing libraries'
    case 'backup_database':
      return 'Backing up database'
    default:
      return fallback
  }
}
